import posthog from 'posthog-js'
import { hasRevokedAnalyticsConsent } from './consent'
import { type EventName, type EventProps, type UserProps } from './constants'

/**
 * Client-side analytics helper
 *
 * Why wrap PostHog?
 * - Consistent API across the app
 * - Built-in error handling
 * - Automatic property injection
 * - Easy to mock in tests
 */

let isInitialized = false

/**
 * The first-party anon id the server set, if present.
 *
 * Kept as a literal rather than importing `ANON_ID_COOKIE` from
 * `anon-id.server.ts` — that module is server-only and importing it here would
 * pull it into the browser bundle. The two must stay in step; the name is
 * asserted in `anon-id.test.ts`.
 */
const ANON_ID_COOKIE = 'dfw_aid'

function readAnonIdCookie(): string | undefined {
	if (typeof document === 'undefined') return undefined
	for (const part of document.cookie.split(';')) {
		const trimmed = part.trim()
		if (trimmed.startsWith(`${ANON_ID_COOKIE}=`)) {
			return trimmed.slice(ANON_ID_COOKIE.length + 1) || undefined
		}
	}
	return undefined
}

export function initAnalyticsClient() {
	// Only initialize once
	if (isInitialized) return

	// Environment-based initialization
	const posthogKey = ENV.POSTHOG_KEY

	if (!posthogKey) {
		console.log('PostHog key not configured for environment:', ENV.MODE)
		return
	}

	// An explicit opt-out is honored without waiting for a consent banner to
	// exist. Absence of a decision is not treated as refusal — that is the
	// current product stance, and the one place to change it if that changes.
	if (hasRevokedAnalyticsConsent()) return

	// Skip initialization on localhost in development
	// if (ENV.MODE === 'development' && window.location.hostname === 'localhost') {
	// 	console.log('PostHog disabled on localhost')
	// 	return
	// }

	/*
	 * Adopt the first-party anon id the server minted on the first response.
	 *
	 * Without this, the server and the browser identify the same visitor
	 * differently: loader events (`directory_search`) and form handlers
	 * (`listing_claim_completed`) carry the first-party id while client events
	 * carry PostHog's own randomly-generated one, and no funnel can span the
	 * two — including the claim funnel, whose start fires here and whose
	 * completion fires on the server. Bootstrapping
	 * makes PostHog use the server's value as its distinct id from its very
	 * first event.
	 *
	 * Read straight from `document.cookie` rather than plumbed through loader
	 * data because this runs before hydration, when loader data is not yet
	 * available. See `anon-id.server.ts` for why the cookie is not httpOnly.
	 */
	const bootstrapDistinctId = readAnonIdCookie()

	try {
		posthog.init(posthogKey, {
			api_host: ENV.POSTHOG_HOST ?? 'https://us.i.posthog.com',
			capture_pageview: false, // We handle pageviews manually for better control
			capture_exceptions: true,
			autocapture: true, // Captures clicks, form submissions automatically
			debug: ENV.MODE === 'development',
			// Respect user privacy
			respect_dnt: true,
			opt_out_capturing_by_default: false,
			...(bootstrapDistinctId
				? { bootstrap: { distinctID: bootstrapDistinctId } }
				: {}),
		})

		// Set global properties that appear on every event
		posthog.register({
			environment: ENV.MODE,
			app_version: ENV.APP_VERSION,
		})

		// Capture first-touch attribution (only set once per user)
		const url = new URL(window.location.href)
		const utmParams = {
			initial_utm_source: url.searchParams.get('utm_source'),
			initial_utm_medium: url.searchParams.get('utm_medium'),
			initial_utm_campaign: url.searchParams.get('utm_campaign'),
			initial_utm_term: url.searchParams.get('utm_term'),
			initial_utm_content: url.searchParams.get('utm_content'),
			initial_referrer: document.referrer || undefined,
			initial_landing_page: window.location.pathname,
		}

		// Only set if values exist (register_once won't override existing values)
		const validUtmParams = Object.fromEntries(
			Object.entries(utmParams).filter(([_, value]) => value),
		)
		if (Object.keys(validUtmParams).length > 0) {
			posthog.register_once(validUtmParams)
		}

		// Make available globally for debugging
		if (ENV.MODE === 'development') {
			;(window as any).posthog = posthog
		}

		isInitialized = true
		//console.log('PostHog initialized successfully')
	} catch (error) {
		console.error('Failed to initialize PostHog:', error)
	}
}

/**
 * Identify a user with PostHog
 * Call this when user logs in or signs up
 */
export function identifyClient(userId: string, properties?: UserProps) {
	if (!isInitialized) return

	try {
		posthog.identify(userId, properties)
	} catch (error) {
		console.error('Failed to identify user:', error)
	}
}

/**
 * Reset user identity
 * Call this when user logs out
 */
export function resetClient() {
	if (!isInitialized) return

	try {
		posthog.reset()
	} catch (error) {
		console.error('Failed to reset PostHog:', error)
	}
}

/**
 * Track an event with consistent properties and deduplication
 */
export function trackClient(
	eventName: EventName,
	properties?: EventProps,
	insertId?: string, // For deduplication
) {
	if (!isInitialized) return

	try {
		// Add current UTM parameters if they exist in URL (for engagement campaigns)
		const url = new URL(window.location.href)
		const currentUtms = {
			current_utm_source: url.searchParams.get('utm_source'),
			current_utm_medium: url.searchParams.get('utm_medium'),
			current_utm_campaign: url.searchParams.get('utm_campaign'),
			current_utm_term: url.searchParams.get('utm_term'),
			current_utm_content: url.searchParams.get('utm_content'),
		}

		const validCurrentUtms = Object.fromEntries(
			Object.entries(currentUtms).filter(([_, value]) => value),
		)

		const eventProperties = {
			...validCurrentUtms,
			...properties,
			...(insertId && { $insert_id: insertId }), // Consistent deduplication
		}

		posthog.capture(eventName, eventProperties)
	} catch (error) {
		console.error('Failed to track event:', error)
	}
}

/**
 * Get the current PostHog distinct ID
 * Useful for passing to server-side events
 */
export function getDistinctId(): string | undefined {
	if (!isInitialized) return undefined

	try {
		return posthog.get_distinct_id()
	} catch (error) {
		console.error('Failed to get distinct ID:', error)
		return undefined
	}
}

/**
 * Update user properties without triggering an event
 */
export function setUserProperties(properties: UserProps) {
	if (!isInitialized) return

	try {
		posthog.people.set(properties)
	} catch (error) {
		console.error('Failed to set user properties:', error)
	}
}
